import React, {useEffect, useState} from 'react';
import {useParams, useNavigate, Link, useLocation} from 'react-router-dom';
import {getProduct, getOrder} from '../services/api';
import {Product, Order} from '../types/api';
import {Checkout} from '../components/Checkout';
import {Shows} from '../components/Shows';
import {SEO} from '../components/SEO';
import {CinemaZoneMovies} from "../components/CinemaZoneMovies";

export const ProductPage: React.FC = () => {
    const defaultProductRefId = '7-days-pass';
    const {refId = defaultProductRefId} = useParams<{ refId: string }>();
    const {orderRefId = ''} = useParams<{ orderRefId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isSuccessPage = location.pathname.includes('/success/');
    const isCancelPage = location.pathname.endsWith('/cancel');
    const [product, setProduct] = useState<Product | null>(null);
    const [order, setOrder] = useState<Order | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [productValidPeriod, setValidPeriod] = useState('');
    const otherProduct = {
        refId: refId === '24-hours-pass' ? '7-days-pass' : '24-hours-pass',
        name: refId === '24-hours-pass' ? '7 Days Pass' : '24 Hours Pass'
    };

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await getProduct(refId);
                setProduct(response.data);
                switch (refId) {
                    case '7-days-pass':
                        setValidPeriod('7 days');
                        break;
                    case '24-hours-pass':
                        setValidPeriod('24 hours');
                        break;
                }
            } catch (err) {
                setError('Failed to load product details');
                console.error('Error loading product:', err);
            } finally {
                setLoading(false);
            }
        };
        if (!refId) {
            navigate('/' + defaultProductRefId);
        } else {
            if (refId === '1-day-pass') {
                navigate('/7-days-pass');
            } else {
                fetchProduct().then();
            }
        }
    }, [refId, navigate]);

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const response = await getOrder(orderRefId);
                if (response.success) {
                    setOrder(response.data);
                }
            } catch (err) {
                setError('Failed to load order details');
                console.error('Error loading order:', err);
            } finally {
                setLoading(false);
            }
        };
        if (orderRefId) {
            fetchOrder().then();
        }
    }, [orderRefId]);

    if (loading) {
        return <div className="text-center py-8">Loading product details...</div>;
    }

    if (error || !product) {
        return <div className="text-center py-8 text-red-600">{error || 'Product not found'}</div>;
    }

    const SuccessMessage = () => (
        <div className="bg-green-50 border-l-4 border-green-500 text-green-800 p-6 rounded-lg shadow-md mb-8">
            <p className="font-semibold mb-4">
                <h2 className="text-2xl font-semibold text-green-600 mb-2">
                    Thank you for your purchase, {order?.name}!
                </h2>
            </p>
            <p className="font-semibold mb-4">
                Your payment of ${order?.product.price} has been successfully processed.
            </p>
            <p>
                What’s next:
                <ul className="space-y-3 text-green-800 list-disc pl-6">
                    <li>Your order reference number is <b>{order?.referenceCode}</b></li>
                    <li>You will receive your <b>{order?.product.name}</b> shortly at your email <b>{order?.email}</b></li>
                    <li>If you have any questions, please contact us at <a href='mailto:viu@appvanz.au'>viu@appvanz.au</a></li>
                </ul>
            </p>
        </div>
    );

    const PurchaseMoreButton = () => (
        <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-base mb-8"
            onClick={() => navigate(`/${product.refId}`)}
        >
            Purchase More
        </button>
    )

    const CancelMessage = () => (
        <div
            className="bg-red-50 border-l-4 border-red-400 text-red-800 p-6 rounded-lg shadow-md mb-8">
            <p className="font-semibold">
                <h2 className="text-2xl font-semibold text-red-600 mb-2">
                    Payment was not completed
                </h2>
            </p>
            <p className="mt-2">
                It looks like you cancelled the checkout process. Don’t worry — <b>your card has not been charged.</b><br/>
                If you’d like, you can return to the checkout page and complete your purchase.
            </p>
        </div>
    );

    const ProductUnavailableMessage = () => (
        <div
            className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-6 rounded-lg shadow-md mb-8">
            <p className="font-semibold">
                <h2 className="text-2xl font-semibold text-yellow-600 mb-2">
                    Product Unavailable
                </h2>
            </p>
            <p className="mt-2">
                We're sorry, but this product is currently unavailable for purchase. Please
                come back later or contact us at <a href='mailto:viu@appvanz.au'>viu@appvanz.au</a>
            </p>
        </div>
    );

    const HowToWatch = () => (
        <div className="mb-8 bg-gray-50 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🎬</span> How to Watch
            </h3>
            <ol className="space-y-3 text-gray-700 list-decimal pl-6">
                <li>
                    <span>Enter your Name and Email, then tap Buy Now</span>
                </li>
                {product.refId !== '7-days-pass' && (
                    <li>
                        <span>Pay using the provided PayID and include the Reference Code</span>
                    </li>
                )}
                <li>
                    <span>After payment, your {product.nameEn} will be emailed to you</span>
                </li>
                <li>
                    <span>Download the Viu app or visit <a href="https://www.viu.com/ott/sg1/en/campaign/redeem" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Viu Redemption Page</a>, sign in, and redeem your code</span>
                </li>
            </ol>
        </div>
    );

    const DownloadAppButton = ({ layout = 'mobile' || 'desktop' }) => (
        <div className={`${layout === 'desktop' ? 'mt-12 pt-12' : 'mt-6 pt-6'} border-t border-gray-200`}>
            <h3 className={`${layout === 'desktop' ? 'text-xl mb-6' : 'text-base mb-3'} font-medium text-gray-900`}>Download Viu App</h3>
            <div className={`flex justify-center ${layout === 'desktop' ? 'space-x-6' : 'space-x-4'}`}>
                <a href="https://apps.apple.com/hk/app/viu/id1036095179?l=en" target="_blank"
                   rel="noopener noreferrer" className="hover:opacity-80">
                    <img src="/images/appstore-icon.webp" alt="Download on App Store" className={`${layout === 'desktop' ? 'h-12' : 'h-10'}`}/>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.viu.phone&referrer=utm_source%3D"
                   target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                    <img src="/images/google-play-icon.webp" alt="Get it on Google Play" className={`${layout === 'desktop' ? 'h-12' : 'h-10'}`}/>
                </a>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100">
            <SEO
                productName={product.nameEn}
                price={product.price}
                description={`Get access to Viu Premium content with our ${product.nameEn}. Watch your favorite shows and movies in HD quality.`}
            />

            <header className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <img src="/images/appvanz-logo.svg" alt="Appvanz Logo" className="h-16 sm:h-24"/>
                        <div className="text-right">
                            <p className="text-sm sm:text-base text-gray-600">Viu Authorized Reseller</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-8">
                    {/* Desktop Layout */}
                    <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12">
                        {/* Left Column: Product Details */}
                        <div className="lg:pr-12 lg:border-r">
                            <div className="text-center mb-8">
                                <a href="https://www.viu.com/" target="_blank" rel="noopener noreferrer"
                                   className="inline-block hover:opacity-90 transition-opacity">
                                    <img src="/images/viu-logo.svg" alt="Viu Logo" className="h-12 mx-auto mb-4"/>
                                </a>
                                <img src="/images/viu-vouchers.webp" alt="Viu Voucher Banner"
                                     className="w-full h-auto rounded-lg mb-6"/>
                                <h1 className="text-3xl font-bold text-gray-900">{product.nameEn}</h1>
                                <p className="text-2xl text-gray-600 mt-2">${product.price} (including GST)</p>
                                <p className="text-2xl mt-6 text-gray-600">
                                    Also available: <Link to={`/${otherProduct.refId}`} className="text-blue-600 hover:underline">{otherProduct.name}</Link>
                                </p>
                            </div>
                            <Shows/>
                        </div>

                        {/* Right Column: Checkout Form */}
                        <div className="lg:pl-12">
                            {isSuccessPage ? (
                                <>
                                    <SuccessMessage />
                                    <PurchaseMoreButton/>
                                </>
                            ) : (

                                !product.available ? (
                                    <ProductUnavailableMessage />
                                ) : (
                                    <>
                                        {isCancelPage && <CancelMessage />}
                                        <HowToWatch />
                                        <Checkout
                                            productName={product.nameEn}
                                            price={product.price}
                                            productRefId={product.refId}
                                        />
                                    </>
                                )

                            )}
                            <DownloadAppButton layout="desktop" />
                        </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="lg:hidden">
                        {isCancelPage && <CancelMessage />}
                        <div className="text-center mb-6">
                            <a href="https://www.viu.com/" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-90 transition-opacity">
                                <img src="/images/viu-logo.svg" alt="Viu Logo" className="h-8 mx-auto mb-2"/>
                            </a>
                            <img src="/images/viu-vouchers.webp" alt="Viu Voucher Banner" className="w-full h-auto rounded-lg mb-4"/>
                            <h1 className="text-xl font-bold text-gray-900">{product.nameEn}</h1>
                            <p className="text-lg text-gray-600 mt-2">${product.price} (including GST)</p>
                            <p className="text-lg mt-4 text-gray-600">
                                Also available: <Link to={`/${otherProduct.refId}`} className="text-blue-600 hover:underline">{otherProduct.name}</Link>
                            </p>
                        </div>

                        {isSuccessPage ? (
                            <>
                                <SuccessMessage />
                                <PurchaseMoreButton />
                                <Shows/>
                            </>
                        ) : (
                            !product.available ? (
                                <>
                                    <ProductUnavailableMessage />
                                    <Shows/>
                                </>
                            ) : (
                                <>
                                    <Shows/>
                                    <HowToWatch />
                                    <Checkout
                                        productName={product.nameEn}
                                        price={product.price}
                                        productRefId={product.refId}
                                    />
                                </>
                            )
                        )}
                        <DownloadAppButton layout="mobile" />
                    </div>
                </div>
            </main>

            {/* Cinema Zone Rental Section */}
            <section className="pb-4 sm:pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <CinemaZoneMovies/>
                </div>
            </section>

            {/* Terms and Conditions Section */}
            <section className="pt-4 pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h2 className="text-m font-semibold text-gray-500 mb-4 flex items-center">
                            Terms and Conditions
                        </h2>
                        <ol className="space-y-2 text-sm text-gray-400">
                            {/*<li className="flex">*/}
                            {/*  <span className="font-medium mr-2">1.</span>*/}
                            {/*<span>Each voucher code is valid for 12 months from the date of purchase.</span>*/}
                            {/*</li>*/}
                            <li className="flex">
                                <span className="font-medium mr-2">1.</span>
                                <span>Voucher codes can only be redeemed in Viu Australia.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">2.</span>
                                <span>Voucher codes are transferable and may be shared, but can only be redeemed once.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">3.</span>
                                <span>Users may redeem multiple voucher codes on the same Viu account.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">4.</span>
                                <span>All sales are final and non-refundable. No refunds will be issued for unused or expired codes.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">5.</span>
                                <span>The voucher grants access to Viu Premium for {productValidPeriod} per code redeemed.</span>
                            </li>
                            <li className="flex">
                                <span className="font-medium mr-2">6.</span>
                                <span>Redemption requires a Viu account. Codes must be entered via the Viu app or website.</span>
                            </li>
                        </ol>
                    </div>
                </div>
            </section>

            <footer className="bg-white border-t border-gray-200 mt-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="text-center text-sm text-gray-600">
                        <p>© 2026 Appvanz Pty Ltd. ABN: 34668403478</p>
                        <p className="mt-1">E-mail: <a href="mailto:viu@appvanz.au" className="text-blue-600 hover:text-blue-800">viu@appvanz.au</a>
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}; 
